'use client';

import { type ReactNode, useId, useState } from 'react';

import { Close } from '@depop/web-thrift-ui/Icons/Close';
import { Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { Button } from '@depop/web-ui-kit/Button';
import { VisuallyHidden } from '@depop/web-ui-kit/VisuallyHidden';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { Container } from '@/components/Container';
import { ExtendedLink } from '@/components/ExtendedLink';

type Props = {
  illustration: ReactNode;
  title: string;
  body: string;
  cta?: { label: string; href: string };
  dismissable?: boolean;
  className?: string;
};

export function CalloutBanner({
  illustration,
  title,
  body,
  cta,
  dismissable = true,
  className,
}: Props) {
  const t = useTranslations('common');
  const [dismissed, setDismissed] = useState(false);
  const titleId = useId();

  function dismiss() {
    setDismissed(true);
  }

  if (dismissed) {
    return null;
  }

  return (
    <section
      className={clsx(styles.banner, className)}
      aria-labelledby={titleId}
    >
      <Container className={styles.inner}>
        {illustration}
        <div className={styles.main}>
          <div className={styles.text}>
            <Heading
              as="span"
              variant="sm"
              className={styles.title}
              id={titleId}
            >
              {title}
            </Heading>
            <span aria-hidden="true" className={styles.separator}>
              ·
            </span>
            <Caption
              as="span"
              variant="lg-regular"
              colour="base"
              className={styles.body}
            >
              {body}
            </Caption>
          </div>
          {cta && (
            <ExtendedLink href={cta.href} className={styles.ctaLink}>
              <Button as="span" size="sm">
                {cta.label}
              </Button>
            </ExtendedLink>
          )}
        </div>
        {dismissable && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={dismiss}
          >
            <Close size="sm" border="regular" />
            <VisuallyHidden>{t('Close')}</VisuallyHidden>
          </button>
        )}
      </Container>
    </section>
  );
}
